import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '@nexdel/core/auth';
import { Loader2 } from '@nexdel/core/utils/icons';

interface MfaSetupProps {
  /** Called once the authenticator code is verified and the factor is enrolled. */
  onEnrolled?: () => void;
  /**
   * Visual tone. 'app' (default) uses the in-app light theme tokens (Profile page);
   * 'login' uses the dark branded auth-screen palette (Login page enrollment).
   */
  tone?: 'app' | 'login';
}

/**
 * Reusable TOTP enrollment flow: generates a secret, shows a QR code (and the secret for
 * manual entry), then verifies a code from the user's authenticator app and enrolls the factor.
 * Used by the forced-enrollment step on the Login screen and the opt-in setup in Profile.
 */
export function MfaSetup({ onEnrolled, tone = 'app' }: MfaSetupProps) {
  const { t } = useTranslation('auth');
  const { startTotpEnrollment, finishTotpEnrollment } = useAuth();

  const [secret, setSecret] = useState<{ secretKey: string; qrCodeUrl: string } | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const isLogin = tone === 'login';
  const cls = {
    muted: isLogin ? 'text-white/50' : 'text-muted-foreground',
    subtle: isLogin ? 'text-white/40' : 'text-muted-foreground',
    label: isLogin ? 'text-white/70' : 'text-foreground',
    code: isLogin ? 'bg-white/10 text-white/80' : 'bg-muted',
    input: isLogin
      ? 'bg-white/5 border-white/10 text-white placeholder-white/20 focus:ring-primary/50'
      : 'bg-background border-border focus:ring-primary/40',
    error: isLogin ? 'text-red-400' : 'text-destructive',
    button: isLogin
      ? 'bg-primary hover:bg-primary/90 text-white'
      : 'bg-primary text-primary-foreground btn-elevated hover:opacity-90',
  };

  const startedRef = useRef(false);
  useEffect(() => {
    // Generate the TOTP secret exactly once. React StrictMode double-invokes effects in dev, and
    // each startTotpEnrollment() mints a NEW secret that overwrites the shared ref — so without this
    // guard the QR shown can diverge from the secret verified at enroll time, yielding INVALID_CODE.
    if (startedRef.current) return;
    startedRef.current = true;
    startTotpEnrollment()
      .then((s) => setSecret(s))
      .catch(() => setSetupError(t('mfaEnroll.setupFailed')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVerify = async (e: FormEvent) => {
    e.preventDefault();
    setVerifyError(null);
    setVerifying(true);
    try {
      await finishTotpEnrollment(code.trim());
      onEnrolled?.();
    } catch {
      setVerifyError(t('mfaEnroll.invalidCode'));
    } finally {
      setVerifying(false);
    }
  };

  if (setupError) {
    return <p className={`text-sm ${cls.error}`}>{setupError}</p>;
  }
  if (!secret) {
    return (
      <div className={`flex items-center gap-2 text-sm ${cls.muted}`}>
        <Loader2 size={14} className="animate-spin" /> {t('mfaEnroll.generating')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ol className={`text-sm space-y-1 list-decimal list-inside ${cls.muted}`}>
        <li>{t('mfaEnroll.step1')}</li>
        <li>{t('mfaEnroll.step2')}</li>
      </ol>

      <div className="flex flex-col items-center gap-3">
        <div className="bg-white p-3 rounded-lg">
          <QRCodeSVG value={secret.qrCodeUrl} size={160} />
        </div>
        <div className="text-center">
          <p className={`text-xs mb-1 ${cls.subtle}`}>{t('mfaEnroll.cantScan')}</p>
          <code className={`text-xs font-mono px-2 py-1 rounded break-all ${cls.code}`}>{secret.secretKey}</code>
        </div>
      </div>

      <form onSubmit={onVerify} className="space-y-3">
        <div>
          <label className={`text-sm font-medium block mb-1.5 ${cls.label}`}>{t('mfaEnroll.codeLabel')}</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            autoFocus
            className={`w-full h-10 border rounded-lg px-3 text-sm text-center tracking-[0.3em] focus:outline-none focus:ring-2 ${cls.input}`}
          />
          {verifyError && <p className={`text-xs mt-1 ${cls.error}`}>{verifyError}</p>}
        </div>
        <button
          type="submit"
          disabled={verifying || code.trim().length < 6}
          className={`h-9 px-4 rounded-lg text-sm font-medium transition disabled:opacity-50 flex items-center gap-2 ${cls.button}`}
        >
          {verifying && <Loader2 size={14} className="animate-spin" />}
          {t('mfaEnroll.verifyEnable')}
        </button>
      </form>
    </div>
  );
}
